import classNames from "classnames";
import debounce from 'debounce-promise';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from "react-redux";
import ReactTable, { SortingRule } from 'react-table-6';
import { HasClassName } from "../../component-utils";
import { useSelector } from "../../hook-utils";
import { ContactBasic, ContactType, Identifier } from "../../model";
import { actions } from '../../store';
import { selectMyAccess, selectMyself } from "../../store/reducers/config";
import { getContactPages, getSelectedContact, selectContacts } from "../../store/reducers/contacts";
import { DirectoryView } from "../../store/reducers/localSettings";
import { selectExtension } from "../../store/reducers/queueAgents";
import { TableColumn, TableRow } from "../../table-utils";
import { ContactForm } from "../AgentView/ContactPage";
import { ContactInfo, ContactMenu, ContactMenuState, hasAccess } from "../ContactsPage";
import { MenuTarget, targetFromEvent } from "../Menu";
import MoreButton from '../MoreButton';
import './ContactPage.scss';


interface Props extends HasClassName {
  view: DirectoryView
  filter: string
}


export default function MobileContactsTable({ className, view, filter }: Props) {
  const [selected, setSelected] = useState<Identifier>()
  const [menuState, setMenuState] = useState<ContactMenuState>()
  const open = useSelector(getSelectedContact)
  const dispatch = useDispatch()
  const contacts = useSelector(selectContacts)
  const data = useMemo(() => contacts ? contacts : [], [contacts])
  const access = useSelector(selectMyAccess)
  const [paging, setPaging] = useState({ page: 0, pageSize: 20 })
  const [curFilter, setCurFilter] = useState(filter)
  const updateFilter = useMemo(() => debounce(setCurFilter, 300), [])
  const pages = useSelector(getContactPages)

  const myId = useSelector(selectMyself)
  const myself = useSelector(s => myId ? selectExtension(s, myId) : undefined)

  const [sorted, setSorted] = useState<SortingRule[]>([{ id: 'description', desc: false }])

  const onMore = useCallback((e: React.SyntheticEvent, item: ContactBasic, target: MenuTarget) => {
    e.preventDefault()
    e.stopPropagation()
    setMenuState({ target, item })
  }, [])

  useEffect(() => {
    updateFilter(filter)
  }, [filter, updateFilter])

  useEffect(() => {
    setPaging(x => ({ ...x, page: 0 }))
  }, [view])

  useEffect(() => {
    dispatch(actions.fetchContactsRequeust(view, {
      page: paging.page,
      pageSize: paging.pageSize,
      orderCol: sorted[0].id,
      orderDesc: sorted[0].desc || false,
      searchText: curFilter,
    }))
  }, [curFilter, dispatch, paging.page, paging.pageSize, sorted, view])

  useEffect(() => {
    return () => {
      //on unload
      dispatch(actions.setSelectedContact(undefined))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!myself || !myId || !access) return null

  return <div className='ContactTableBody'>
    {!(open && view.contactType !== undefined && hasAccess(open.id, view.contactType, access)) && <ReactTable className={classNames('ContactTable', className, 'MobileContactTable')}
      data={data}
      columns={getColumns(onMore)}
      showPagination={true}
      page={paging.page}
      pageSize={paging.pageSize}
      pages={pages}
      minRows={0}
      loading={!contacts}
      NoDataComponent={() => null}
      getTrProps={(_: any, row?: TableRow<ContactBasic>): React.HtmlHTMLAttributes<HTMLTableRowElement> | undefined => row && ({
        className: selected && row.original.id === selected.id && row.original.server === selected.server ? 'selected' : undefined,
        onContextMenu(e) {
          setMenuState({ target: targetFromEvent(e), item: row.original })
        },
        onClick() {
          if (row.original.business) {
            dispatch(actions.setSelectedNumber(row.original.business))
          } else if (row.original.mobile) {
            dispatch(actions.setSelectedNumber(row.original.mobile))
          } else if (row.original.home) {
            dispatch(actions.setSelectedNumber(row.original.home))
          }

          setSelected(row.original)
        },
      })}
      manual
      onFetchData={({ page, pageSize }) => {
        setPaging({ page, pageSize })
      }}
      sorted={sorted}
      onSortedChange={(newSort, column) => {
        setSorted([newSort[0]])
      }}
    />}

    {menuState && <ContactMenu state={menuState}
      onClose={() => setMenuState(undefined)}
      onOpen={(item) => {
        dispatch(actions.setSelectedContact(item))
      }}
      onNew={(item) => {
        dispatch(actions.setSelectedContact({ id: '', server: '' }))
      }} />}

    {open && view.contactType !== undefined && hasAccess(open.id, view.contactType, access) && renderSelection(open.id, view.contactType, () => {
      dispatch(actions.setSelectedContact(undefined))
    })}
  </div>
}

function renderSelection(contact: Identifier, contactType: ContactType, onClose: () => void) {
  return <div className='ContactPage'>
    <ContactForm contactId={contact.id} contactType={contactType} server={contact.server} onCancel={() => onClose()} />
  </div>
}

function getColumns(onMore: (e: React.MouseEvent, item: ContactBasic, target: MenuTarget) => void): Array<TableColumn<ContactBasic>> {
  return [{
    id: 'description',
    Header: 'Details',
    accessor: a => a.description,
    Cell: ({ original: row }) => <div className='info'>
      <ContactInfo contact={row} />
    </div>,
  }, {
    id: 'actions',
    Cell: ({ original: data }) => <div className='actions'>
      <MoreButton className='menu-button' onClick={e => {
        e.stopPropagation()
        onMore(e, data, targetFromEvent(e))
      }
      } />
    </div>,
    width: 40,
    sortable: false,
    resizable: false,
  },
  ]
}
